import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { fadeInFadeOutTw } from '~/styles/mixins'
import { Link, useMatches } from '@remix-run/react'
import { trackEvent } from '~/utils/tracking'
import { Button } from '~/components/general/Button'
import { DownArrowIcon } from '~/components/icons'
import { createOptimizedContentfulImage } from '~/libs/contentful/helpers'
import { NavItem } from '../DesktopHeader'
import { Asset, AssetFields, Entry } from 'contentful'
import { TypeNavigationLink, TypePromoBannerSkeleton } from '~/types/contentful'

export type ServiceMenuProps = {
  variant?: string
  isActive: boolean
  setDropdown: (value: boolean) => void
  userAuthorized?: boolean
  menuData?: NavItem
  checkIfDarkBg?: boolean
}

type MatchTotalJob = {
  data: {
    totalJobSearchPromo: Entry<TypePromoBannerSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  }
}

export const ServiceMenu = ({
  isActive,
  setDropdown,
  userAuthorized,
  menuData,
  checkIfDarkBg,
}: ServiceMenuProps) => {
  const matches = useMatches()

  const {
    data: {
      data: { totalJobSearchPromo },
    },
  } = matches.find((match) => (match?.data as MatchTotalJob)?.data?.totalJobSearchPromo) as {
    data: MatchTotalJob
  }

  const [activeTab, setActiveTab] = useState<number>(0)
  const wrapperRef = useRef<HTMLUListElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const setTab = (id: number) => {
    setActiveTab(id)
  }

  const isTabActive = (id: number) => {
    return id === activeTab
  }
  const handleMenuToggle = () => {
    setDropdown(!isActive)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      event.target === buttonRef.current ||
      buttonRef.current?.contains(event.target as HTMLDivElement)
    )
      return
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as HTMLDivElement)) {
      setDropdown(false)
    }
  }

  const getClassList = ({ service }: { service: NavItem }) => {
    return [
      service.label?.toLowerCase() === 'soon' ? 'locked' : '',
      service.label?.toLowerCase() === 'new' ? 'isNew' : '',
      service.color ? 'service-colored' : '',
    ].join(' ')
  }

  const handleTabItemClick = (idx: number, categoryName: string) => {
    trackEvent('toggle_website_menu', { click_menu_category: categoryName })
    setTab(idx)
  }

  const handleServiceItemClick = (serviceName: string) => {
    trackEvent('click_website_menu', { click_menu_service: serviceName })
    setDropdown(false)
  }

  useEffect(() => {
    if (isActive) trackEvent('open_website_menu')
  }, [isActive])

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false)

    return () => {
      document.removeEventListener('click', handleClickOutside, false)
    }
  }, [])

  return (
    <>
      <div className="relative inline-block" ref={buttonRef}>
        <Button
          text={menuData?.title}
          iconRight={
            <DownArrowIcon
              className={classNames('transition-transform duration-200', isActive && 'rotate-180')}
            />
          }
          onClick={handleMenuToggle}
          color={'transparent'}
          className={classNames(
            'w-fit !text-mobile-s',
            checkIfDarkBg ? '!text-white hover:!text-neutral-100' : '!text-neutral-100',
          )}
        />
      </div>
      <ul
        style={{
          borderRadius: '0px 0px 42px 42px',
        }}
        ref={wrapperRef}
        className={`max-md:left-2 max-md:w-50 rounded-bl-[42px] max-md:bg-neutral-100 hover:max-md:text-white bg-neutral-5 pt-[calc(84px_+_20px)] -z-1 shadow-header overflow-hidden absolute top-0 w-full left-1/2 flex -translate-x-1/2 justify-around px-6 pb-8 ${fadeInFadeOutTw(
          isActive,
        )}`}
      >
        <div className="grid items-start gap-8 w-full grid-cols-[0.5fr_1fr] max-w-[1420px]">
          <div className="grid gap-2">
            {menuData?.groups
              ?.map((group) => group?.fields)
              .map((item, idx: number) => (
                <div
                  key={idx}
                  onClick={() => handleTabItemClick(idx, item?.title as string)}
                  className={classNames(
                    'bg-neutral-5 py-7 px-10 rounded-3xl text-mobile-s cursor-pointer max-h-[90px] transition-all duration-1000 ease overflow-hidden [&.tab-active]:bg-white [&.tab-active]:max-h-[500px]',
                    { 'tab-active': isTabActive(idx) },
                  )}
                >
                  <div className="text-s text-neutral-90">{item?.title as string}</div>
                  {isTabActive(idx) ? (
                    <div className="text-neutral-60 mt-2 text-xs">
                      {item?.description as string}
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              ))}
            {totalJobSearchPromo?.fields && (
              <div
                className="rounded-[24px] flex flex-row justify-around h-[233px] max-h-[233px]"
                style={{ background: totalJobSearchPromo?.fields?.backgroundColour }}
              >
                <div className="flex flex-col justify-between mb-[40px] mt-[40px] w-[220px] ml-[40px]">
                  <div className="text-s text-neutral-100">
                    {totalJobSearchPromo?.fields?.titleText}
                  </div>
                  <Button
                    text={totalJobSearchPromo?.fields.ctaButtonText}
                    type={'link'}
                    target={'_self'}
                    color={'white'}
                    href={totalJobSearchPromo?.fields.ctaButtonLink}
                    className={`w-fit !h-[52px] !text-blue-50`}
                    onClick={() => {
                      trackEvent('click_website_menu', {
                        website_host: 'career.io',
                        click_menu_services: 'banner',
                        cta: 'Find Out How',
                        target: totalJobSearchPromo?.fields.ctaButtonLink as string,
                      })
                    }}
                  />
                </div>
                <div className="flex justify-center items-center w-[244px]">
                  <img
                    className="object-contain max-h-[230px]"
                    src={createOptimizedContentfulImage(
                      totalJobSearchPromo?.fields?.image?.fields as AssetFields,
                    )}
                    alt={totalJobSearchPromo?.fields?.image?.fields?.title}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white grid grid-cols-[0.956fr_1fr] overflow-y-auto p-8 main-menu max-h-[83vh] gap-y-2 gap-x-8 overscroll-none scrollbar-hide rounded-3xl">
            {menuData?.groups &&
              (
                menuData.groups[activeTab]?.fields?.links as TypeNavigationLink<
                  'WITHOUT_UNRESOLVABLE_LINKS',
                  string
                >[]
              )
                ?.map((item) => {
                  return {
                    ...item?.fields,
                  }
                })
                .map((service, id: number) => (
                  <li key={`${activeTab}_${id}`} className="text-s list-none">
                    {service.isAuthRequired && !userAuthorized ? (
                      <a
                        className={`[&.isNew]:bg-neutral-5 group max-md:transition-colors max-md:duration-200 max-md:block max-md:w-[300px] max-md:h-11 hover:[&:not(.locked)]:bg-neutral-10 [&.service-colored]:text-white [&.service-colored]:[&>div]:text-inherit flex p-[6px_6px_11px] rounded-[10px] h-full cursor-pointer transition-colors ${getClassList(
                          { service: service },
                        )}`}
                        href="/app/auth/sign-in"
                        style={{
                          background: service.color as string,
                        }}
                        onClick={() => handleServiceItemClick(service.title as string)}
                      >
                        <div className="text-neutral-50">
                          <img
                            className="w-10 h-10 p-2 object-contain rounded-md"
                            src={createOptimizedContentfulImage(
                              (service?.icon as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)
                                ?.fields,
                            )}
                            alt=""
                          />
                        </div>

                        <div>
                          <div className="text-xs mt-1 flex gap-1">
                            <span className="group-has-[.locked]:text-neutral-50">
                              {service.title as string}
                            </span>
                            {service.label && (
                              <div className="service-status text-mobile-xs py-1 px-2 text-neutral-60 inline-flex bg-neutral-10 capitalize items-center rounded-[10px] group:has-[.isNew]:bg-white group:has-[.isNew]:text-neutral-50">
                                {service.label as string}
                              </div>
                            )}
                          </div>
                          <div className="service-description text-xs max-w-[280px] mt-1 text-neutral-40 max-xl:w-50">
                            {service.description as string}
                          </div>
                        </div>
                      </a>
                    ) : service.isAuthRequired && userAuthorized ? (
                      <a
                        className={`[&.isNew]:bg-neutral-5 group  max-md:transition-colors max-md:duration-200 max-md:block max-md:w-[300px] max-md:h-11 hover:[&:not(.locked)]:bg-neutral-10 [&.service-colored]:text-white [&.service-colored]:[&>div]:text-inherit flex p-[6px_6px_11px] rounded-[10px] h-full cursor-pointer transition-colors ${getClassList(
                          { service: service },
                        )}`}
                        href={service.link as string}
                        style={{
                          background: service.color as string,
                        }}
                        onClick={() => handleServiceItemClick(service.title as string)}
                      >
                        <div className="text-neutral-50">
                          <img
                            className="w-10 h-10 p-2 object-contain rounded-md"
                            src={createOptimizedContentfulImage(
                              (service?.icon as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)
                                ?.fields,
                            )}
                            alt=""
                          />
                        </div>
                        <div>
                          <div className="text-xs mt-1 flex gap-1">
                            <span className="group-has-[.locked]:text-neutral-50">
                              {service.title as string}
                            </span>
                            {service.label && (
                              <div className="text-mobile-xs py-1 px-2 text-neutral-60 inline-flex bg-neutral-10 capitalize items-center rounded-[10px] service-status group:has-[.isNew]:bg-white group:has-[.isNew]:text-neutral-50">
                                {service.label as string}
                              </div>
                            )}
                          </div>
                          <div className="text-xs max-w-[280px] mt-1 text-neutral-40 max-xl:w-50">
                            {service.description as string}
                          </div>
                        </div>
                      </a>
                    ) : (
                      <Link
                        className={`[&.isNew]:bg-neutral-5 group max-md:transition-colors max-md:duration-200 max-md:block max-md:w-[300px] max-md:h-11 hover:[&:not(.locked)]:bg-neutral-10 [&.service-colored]:text-white [&.service-colored]:[&>div]:text-inherit flex p-[6px_6px_11px] rounded-[10px] h-full cursor-pointer transition-[background-color_0.2s_ease,color_0.2s_ease] ${getClassList(
                          { service: service },
                        )}`}
                        to={service.link as string}
                        style={{
                          background: service.color as string,
                        }}
                        onClick={() => handleServiceItemClick(service.title as string)}
                        reloadDocument={service?.link ? service.link.includes('/mystory') : false}
                      >
                        <div className="text-neutral-50">
                          <img
                            className="w-10 h-10 p-2 object-contain rounded-md"
                            src={createOptimizedContentfulImage(
                              (service?.icon as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)
                                ?.fields,
                            )}
                            alt=""
                          />
                        </div>

                        <div>
                          <div className="text-xs mt-1 flex gap-1">
                            <span className="group-has-[.locked]:text-neutral-50 text-neutral-100">
                              {service.title as string}
                            </span>
                            {service.label && (
                              <div className="service-status text-mobile-xs py-1 px-2 text-neutral-60 inline-flex bg-neutral-10 capitalize items-center rounded-[10px] group:has-[.isNew]:bg-white group:has-[.isNew]:text-neutral-50">
                                {service.label as string}
                              </div>
                            )}
                          </div>
                          <div className="text-xs max-w-[280px] mt-1 text-neutral-40 max-xl:w-50">
                            {service.description as string}
                          </div>
                        </div>
                      </Link>
                    )}
                  </li>
                ))}
          </div>
        </div>
      </ul>
    </>
  )
}
